<template>
  <div ref="component">
    <div class="col-12">
      <span class="text-md text-weight-medium text-neutral"> KYC </span>
      <q-separator class="q-mb-xs" />
    </div>

    <div class="q-mb-lg">
      <div class="col-12 text-md text-weight-medium">Primary Contact</div>
      <div class="q-pa-xs">
        <div class="row q-col-gutter-lg">
          <div
            v-for="{ name, label, spacing } in directorDetailsColumns"
            :key="name"
            :class="spacing"
          >
            <span class="text-xs text-weight-medium text-neutral ellipsis">
              {{ label }}
              <span
                v-if="!!isRequiredField({ fieldName: name })"
                class="text-negative"
                >*</span
              >
            </span>
          </div>
        </div>
        <q-separator color="neutral-40" class="q-mt-xxs q-mb-sm" />
        <div
          v-if="computedDirectorsModelValue?.[0]"
          class="row q-col-gutter-lg"
        >
          <div
            v-for="{ name, spacing } in directorDetailsColumns"
            :key="name"
            :class="spacing"
          >
            <PInput
              v-if="
                name === 'credit_score' && computedInitDirectors?.[0]?.[name]
              "
              v-model.number="computedDirectorsModelValue[0][name]"
              :placeholder="'Enter ' + name"
              disable
              type="number"
              input-class="text-xs"
            />
            <PInput
              v-else-if="
                name === 'credit_score' && !computedInitDirectors?.[0]?.[name]
              "
              v-model.number="computedDirectorsModelValue[0][name]"
              :placeholder="
                computedInitDirectors?.[0]?.pan == null ||
                !computedInitDirectors?.[0]?.pan?.length
                  ? 'Autofilled'
                  : 'Enter ' + name
              "
              type="number"
              input-class="text-xs"
              :validations="{ gte: 350, lte: 900 }"
            />
            <PInput
              v-else-if="name === 'name'"
              v-model.trim="computedDirectorsModelValue[0][name]"
              :placeholder="'Enter ' + name"
              required
              :disable="isCompleted || isGstData"
              input-class="text-xs text-capitalize"
              :error="!!computedDirectorsModelValue[0].error"
            />
            <div v-else-if="name === 'phone'">
              <PInput
                v-model.trim="computedDirectorsModelValue[0][name]"
                :placeholder="'Enter ' + name"
                :rules="[
                  () => isDuplicate(computedDirectorsModelValue[0][name], name),
                ]"
                maxlength="10"
                :validations="{
                  required: true,
                  type: 'phoneNumber',
                  length: 10,
                }"
                :disable="computedDirectorsModelValue[0].is_phone_verified"
                input-class="text-xs"
                hide-bottom-space
              >
                <template #after>
                  <div v-if="isCompleted" class="q-mt-xxs">
                    <div
                      v-if="computedDirectorsModelValue[0].is_phone_verified"
                    >
                      <CheckIcon class="icon text-success-70" />
                      <PTooltip>Verified</PTooltip>
                    </div>
                    <div v-else>
                      <ExclamationIcon class="icon text-warning-60" />
                      <PTooltip>Unverified</PTooltip>
                    </div>
                  </div>
                </template>
              </PInput>
              <PBtn
                v-if="showVerifyBtn(computedDirectorsModelValue[0])"
                label="Verify"
                flat
                dense
                class="q-pa-none"
                @click="
                  () =>
                    verifyVerificationCodeModal({
                      director: computedDirectorsModelValue[0],
                    })
                "
              />
            </div>

            <PInput
              v-else-if="name === 'pan'"
              v-model.trim="computedDirectorsModelValue[0][name]"
              :placeholder="'Enter ' + name"
              :rules="[
                () => isDuplicate(computedDirectorsModelValue[0][name], name),
                (val) => validatePan(val),
              ]"
              maxlength="10"
              :validations="{ length: 10 }"
              :disable="isPanDisabled(0)"
              input-class="text-xs"
              @update:model-value="
                (value) => {
                  computedDirectorsModelValue[0][name] = value.toUpperCase();
                }
              "
            >
              <template #after>
                <div v-if="isCompleted" class="q-mt-xxs">
                  <component
                    :is="
                      getPanStatusComponent({
                        panStatus: computedDirectorsModelValue[0].pan_status,
                        panAadhaarStatus:
                          computedDirectorsModelValue[0].pan_aadhaar_status,
                      }).icon
                    "
                    :class="
                      getPanStatusComponent({
                        panStatus: computedDirectorsModelValue[0].pan_status,
                        panAadhaarStatus:
                          computedDirectorsModelValue[0].pan_aadhaar_status,
                      }).iconClass
                    "
                  ></component>
                  <PTooltip>{{
                    getPanStatusComponent({
                      panStatus: computedDirectorsModelValue[0].pan_status,
                      panAadhaarStatus:
                        computedDirectorsModelValue[0].pan_aadhaar_status,
                    }).tooltipText
                  }}</PTooltip>
                </div>
              </template>
            </PInput>
            <PInput
              v-else-if="name === 'aadhar'"
              v-model.trim="computedDirectorsModelValue[0][name]"
              :placeholder="'Enter ' + name"
              :rules="[
                () => isDuplicate(computedDirectorsModelValue[0][name], name),
              ]"
              :validations="{ length: 12, type: 'integer' }"
              maxlength="12"
              :disable="isAadhaarDisabled(0)"
              input-class="text-xs"
            />

            <PTooltip
              v-if="computedDirectorsModelValue[0][name]"
              class="text-capitalize"
              >{{
                getTooltipLabel(
                  name,
                  computedDirectorsModelValue[0][name],
                  computedDirectorsModelValue[0].error,
                )
              }}</PTooltip
            >
          </div>
        </div>
      </div>
    </div>

    <div v-if="computedDirectorsModelValue.length - 1 > 0">
      <div class="col-12 text-md text-weight-medium">Additional Directors</div>

      <div class="q-pa-xs">
        <div class="row q-col-gutter-lg">
          <div
            v-for="{ name, label, spacing } in directorDetailsColumns"
            :key="name"
            :class="spacing"
          >
            <span class="text-xs text-weight-medium text-neutral ellipsis">
              {{ label }}
              <span
                v-if="
                  !!isRequiredField({
                    fieldName: name,
                    additionalDirectors: true,
                  })
                "
                class="text-negative"
              >
                *
              </span>
            </span>
          </div>
        </div>
        <q-separator color="neutral-40" class="q-mt-xxs q-mb-sm" />
        <div
          v-for="({}, index) in computedDirectorsModelValue.length - 1"
          :key="index"
          class="row q-col-gutter-x-lg q-my-sm"
        >
          <div
            v-for="{ name, spacing } in directorDetailsColumns"
            :key="name"
            :class="spacing"
          >
            <PInput
              v-if="
                name === 'credit_score' &&
                computedInitDirectors?.[index + 1]?.[name]
              "
              v-model.number="computedDirectorsModelValue[index + 1][name]"
              :placeholder="'Enter ' + name"
              disable
              type="number"
              input-class="text-xs"
            />
            <PInput
              v-else-if="
                name === 'credit_score' &&
                !computedInitDirectors?.[index + 1]?.[name]
              "
              v-model.number="computedDirectorsModelValue[index + 1][name]"
              :placeholder="
                computedInitDirectors?.[index + 1]?.pan == null
                  ? 'Autofilled'
                  : 'Enter ' + name
              "
              type="number"
              input-class="text-xs"
              :validations="{ gte: 350, lte: 900 }"
            />
            <PInput
              v-else-if="name === 'name'"
              v-model.trim="computedDirectorsModelValue[index + 1][name]"
              :placeholder="'Enter ' + name"
              required
              :disable="isCompleted || isGstData"
              input-class="text-xs text-capitalize"
              :error="!!computedDirectorsModelValue[index + 1].error"
            />
            <div v-else-if="name === 'phone'">
              <PInput
                v-model.trim="computedDirectorsModelValue[index + 1][name]"
                :placeholder="'Enter ' + name"
                :rules="[
                  () =>
                    isDuplicate(
                      computedDirectorsModelValue[index + 1][name],
                      name,
                    ),
                ]"
                maxlength="10"
                :validations="{
                  type: 'phoneNumber',
                  length: 10,
                }"
                :disable="
                  computedDirectorsModelValue[index + 1].is_phone_verified
                "
                input-class="text-xs"
                hide-bottom-space
              >
                <template #after>
                  <div v-if="isCompleted" class="q-mt-xxs">
                    <div
                      v-if="
                        computedDirectorsModelValue[index + 1].is_phone_verified
                      "
                    >
                      <CheckIcon class="icon text-success-70" />
                      <PTooltip>Verified</PTooltip>
                    </div>
                    <div
                      v-else-if="!!computedDirectorsModelValue[index + 1].phone"
                    >
                      <ExclamationIcon class="icon text-warning-60" />
                      <PTooltip>Unverified</PTooltip>
                    </div>
                  </div>
                </template>
              </PInput>
              <PBtn
                v-if="showVerifyBtn(computedDirectorsModelValue[index + 1])"
                label="Verify"
                flat
                dense
                class="q-pa-none"
                @click="
                  () =>
                    verifyVerificationCodeModal({
                      director: computedDirectorsModelValue[index + 1],
                    })
                "
              />
            </div>

            <PInput
              v-else-if="name === 'pan'"
              v-model.trim="computedDirectorsModelValue[index + 1][name]"
              :placeholder="'Enter ' + name"
              :rules="[
                () =>
                  isDuplicate(
                    computedDirectorsModelValue[index + 1][name],
                    name,
                  ),
                (val) => validatePan(val),
              ]"
              maxlength="10"
              :validations="{ length: 10 }"
              :disable="isPanDisabled(index + 1)"
              input-class="text-xs"
              @update:model-value="
                (value) => {
                  computedDirectorsModelValue[index + 1][name] =
                    value.toUpperCase();
                }
              "
            >
              <template #after>
                <div v-if="isCompleted" class="q-mt-xxs">
                  <component
                    :is="
                      getPanStatusComponent({
                        panStatus:
                          computedDirectorsModelValue[index + 1].pan_status,
                        panAadhaarStatus:
                          computedDirectorsModelValue[index + 1]
                            .pan_aadhaar_status,
                      }).icon
                    "
                    :class="
                      getPanStatusComponent({
                        panStatus:
                          computedDirectorsModelValue[index + 1].pan_status,
                        panAadhaarStatus:
                          computedDirectorsModelValue[index + 1]
                            .pan_aadhaar_status,
                      }).iconClass
                    "
                  ></component>
                  <PTooltip>{{
                    getPanStatusComponent({
                      panStatus:
                        computedDirectorsModelValue[index + 1].pan_status,
                      panAadhaarStatus:
                        computedDirectorsModelValue[index + 1]
                          .pan_aadhaar_status,
                    }).tooltipText
                  }}</PTooltip>
                </div>
              </template>
            </PInput>
            <PInput
              v-else-if="name === 'aadhar'"
              v-model.trim="computedDirectorsModelValue[index + 1][name]"
              :placeholder="'Enter ' + name"
              :rules="[
                () =>
                  isDuplicate(
                    computedDirectorsModelValue[index + 1][name],
                    name,
                  ),
              ]"
              :validations="{ length: 12, type: 'integer' }"
              maxlength="12"
              :disable="isAadhaarDisabled(index + 1)"
              input-class="text-xs"
            />

            <PTooltip
              v-if="computedDirectorsModelValue[index + 1][name]"
              class="text-capitalize"
            >
              {{
                getTooltipLabel(
                  name,
                  computedDirectorsModelValue[index + 1][name],
                  computedDirectorsModelValue[index + 1].error,
                )
              }}
            </PTooltip>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { putValidateDirectorName } from "src/api/user";
import { unref, watch } from "vue";
import CheckIcon from "assets/icons/check-round-outline.svg";
import ExclamationIcon from "assets/icons/exclamation-round.svg";
import { useContactVerification } from "../composables/contact-verification";

const { user } = storeToRefs(useUserStore());
const { verifyVerificationCodeModal } = useContactVerification({
  user,
  is_director: true,
});

const computedInitDirectors = computed(() =>
  unref(user)?.businessDetails?.directors?.sort(
    (a, b) => b.is_primary - a.is_primary,
  ),
);

const showVerifyBtn = (director) => {
  return props.isCompleted && !director.is_phone_verified && !!director.phone;
};

const getTooltipLabel = (field, value, error) => {
  if (field === "name" && !!error) {
    return value + " | Name mismatch";
  } else return value;
};

const getPanStatusComponent = ({ panStatus, panAadhaarStatus }) => {
  if (panStatus == null) {
    return {
      icon: ExclamationIcon,
      iconClass: "icon text-warning-60",
      tooltipText: "No Status",
    };
  }

  let panAadhaarStatusText = "";

  if (panAadhaarStatus != null) {
    panAadhaarStatusText = panAadhaarStatus
      ? " & Aadhaar Linked"
      : " & Aadhaar Unlinked";
  }

  if (panStatus) {
    return {
      icon: CheckIcon,
      iconClass: "icon text-success-70",
      tooltipText: `Valid${panAadhaarStatusText}`,
    };
  }

  return {
    icon: ExclamationIcon,
    iconClass: "icon text-error-60",
    tooltipText: `Invalid${panAadhaarStatusText}`,
  };
};

const isRequiredField = ({ fieldName, additionalDirectors }) => {
  return ![
    "aadhar",
    "credit_score",
    "pan",
    additionalDirectors ? "phone" : "",
  ].includes(fieldName);
};

const directorDetailsColumns = ref([
  {
    name: "name",
    label: "Name",
    spacing: "col-2",
  },
  {
    name: "phone",
    label: "Contact No.",
    spacing: "col-3",
  },
  {
    name: "pan",
    label: "PAN",
    spacing: "col-3",
  },
  {
    name: "aadhar",
    label: "Aadhar",
    spacing: "col-2",
  },
  {
    name: "credit_score",
    label: "Credit Score",
    spacing: "col-2",
  },
]);

const props = defineProps({
  directors: {
    type: Array,
    default: () => {},
  },
  isCompleted: {
    type: Boolean,
  },
  isGstData: {
    type: Boolean,
  },
});

const isPanDisabled = (index) => {
  const director = computedDirectorsModelValue.value[index];

  return props.isCompleted && Boolean(director?.pan_status);
};

const isAadhaarDisabled = (index) => {
  return (
    props.isCompleted && !!computedInitDirectors.value?.[index]?.aadhar?.length
  );
};

const validateDirectorName = async (director) => {
  if (director) {
    try {
      await putValidateDirectorName({
        director: { _id: director._id, name: director.name },
      });

      return true;
    } catch {
      director.error = "Name mismatch";

      return false;
    }
  }

  return true;
};

const validatePan = (value) => {
  if (!value) {
    return;
  }

  return /^[A-Z]{5}\d{4}[A-Z]$/.test(value);
};

const isDuplicate = (value, prop) => {
  return value
    ? unref(computedDirectorsModelValue).filter(
        (director) => director[prop] == value,
      ).length < 2 || "Duplicate"
    : true;
};

const component = ref(null);

const emit = defineEmits(["update:directors"]);

defineExpose({
  component,
});

const computedDirectorsModelValue = computed({
  get() {
    return props.directors;
  },
  set(value) {
    emit("update:directors", value);
  },
});

let validationTriggered = false;

watch(
  [() => props.isCompleted, () => props.directors],
  ([isCompleted, directors]) => {
    if (
      isCompleted &&
      Array.isArray(directors) &&
      directors.length > 0 &&
      directors.every((d) => !!d.name) &&
      !validationTriggered
    ) {
      validationTriggered = true;

      for (const director of unref(computedDirectorsModelValue)) {
        validateDirectorName(director);
      }
    }
  },
  { immediate: true, deep: true },
);
</script>
